import classNames from 'classnames/bind';
import MouseTrap from 'mousetrap';
import { ReactNode, RefObject, useEffect } from 'react';

import { Exit } from '../../assets/icons/Icons';
import useClickOutside from '../../hooks/useClickOutside/useClickOutside';
import Button from '../Button/Button';
import Styles from './Modal.module.scss';

const cx = classNames.bind(Styles);

interface Props {
  /** Whether to show or hide the modal  */
  show?: boolean;
  /** Whether to close dialog on outside clicks */
  clickOutside?: boolean;
  /** Title of the Modal */
  title?: string;
  /** Primary button text */
  primaryButtonText?: string;
  /** onClick function for the primary button */
  primaryButtonOnClick?: () => void;
  /** Content of the modal */
  children: ReactNode;
  /** Secondary button text */
  secondaryButtonText?: string;
  /** onClick function for the secondary button */
  secondaryButtonOnClick?: () => void;
  /** Close function for the Modal */
  onClose?: () => void;
  /** Whether or not to show the modal close 'X' button */
  showClose?: boolean;
  /** Custom bottom element to override primary/secondary buttons */
  customBottomElement?: ReactNode;
  /** Make the modal take up the entire screen on mobile */
  fullScreen?: boolean;
}

const Modal = ({
  show = false,
  onClose,
  primaryButtonOnClick,
  primaryButtonText,
  secondaryButtonText,
  secondaryButtonOnClick,
  customBottomElement,
  children,
  clickOutside,
  showClose,
  title,
  fullScreen = false,
}: Props) => {
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [show]);

  useEffect(() => {
    if (clickOutside || (clickOutside === undefined && showClose)) {
      MouseTrap.bind('escape', (e) => {
        if (e?.preventDefault) {
          e.preventDefault();
        }

        if (show) {
          onClose?.();
        }
      });
    }

    return () => {
      MouseTrap.unbind('escape');
    };
  }, [clickOutside, show, showClose]);

  const ref = useClickOutside(onClose, show) as RefObject<HTMLDivElement>;

  return (
    <dialog className={cx('modal')} data-testid='modal-container' open={show}>
      <div
        className={cx('modal__content', { 'modal__content--full-screen': fullScreen })}
        ref={clickOutside ? ref : undefined}>
        {showClose || title ? (
          <div className={cx('modal__header')}>
            {title && <h3 className={cx('modal__header__title')}>{title}</h3>}
            {showClose && onClose ? (
              <div className={cx('modal__times')} onClick={() => onClose()} data-testid='close-button'>
                {Exit}
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          className={cx('modal__children', { 'modal__children--no-close': !showClose })}
          data-testid='modal-children'>
          {children}
        </div>

        {customBottomElement && <div className={cx('modal__bottom-content')}>{customBottomElement}</div>}

        {!customBottomElement && (primaryButtonText || secondaryButtonText) ? (
          <div className={cx('modal__buttons', 'modal__bottom-content')}>
            {secondaryButtonText ? (
              <Button type='secondary' onClick={secondaryButtonOnClick ? () => secondaryButtonOnClick() : undefined}>
                {' '}
                {secondaryButtonText}{' '}
              </Button>
            ) : null}
            {primaryButtonText ? (
              <Button onClick={primaryButtonOnClick ? () => primaryButtonOnClick() : undefined}>
                {' '}
                {primaryButtonText}{' '}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </dialog>
  );
};

export default Modal;
